import React, { useState } from 'react';
import { SCRUBBER_API_BASE } from '../api/config';
import { csrfFetch } from '../api/csrf';
import useAgentProgress from '../lib/useAgentProgress';
import AgentThinkingPanel from './AgentThinkingPanel';

// Inline activity panel — polls the Scrubber agent's progress events for the
// upload's submission_id and renders the calm grey "Reading the universe file
// → Identified as ODAG → Validating each row" timeline. Hides itself when no
// events have been written (e.g., feature flag PSI_AGENT_THINKING=off, or the
// direct portal upload path that has not been wired into emitProgress yet).
function ScrubberAgentActivity({ submissionId }) {
  const { events, status } = useAgentProgress('scrubber_submission', submissionId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Activity" />;
}

export default function ResultLayout({
  children,
  categoryFilters,
  selectedCategories,
  onCategoryToggle,
  onlyShowErroredRows,
  onToggleErrors,
  issueCounts,
  universe,
  timeliness,
  totalRows,
  totalErrorCount,
  errors,
  selectedTable,
  userEmail,
  onExport,
  submissionId
}) {
  const totalIssues = Object.values(issueCounts).reduce((a, b) => a + b, 0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  console.log('🛠 DEBUG — ResultLayout received:', {
    issueCounts,
    totalIssues,
    issueCountsKeys: Object.keys(issueCounts),
    issueCountsValues: Object.values(issueCounts)
  });

  const handleSubmitFinal = async () => {
    console.log('🛠 DEBUG — Submit as Final button clicked!', {
      totalIssues,
      issueCounts
    });
    
    try {
      let shouldSubmit = false;
      
      if (totalIssues > 0) {
        console.log('🛠 DEBUG — Showing error warning dialog');
        shouldSubmit = window.confirm(`You have ${totalIssues} validation errors. Are you sure you want to submit as final?`);
      } else {
        console.log('🛠 DEBUG — Showing clean confirmation dialog');
        shouldSubmit = window.confirm('Submit this universe as final to Compliance?');
      }
      
      if (shouldSubmit) {
        console.log('🛠 DEBUG — User confirmed submission, calling API...');
        
        // Check if this is a resubmission
        const resubmissionContext = sessionStorage.getItem('resubmissionContext');
        let response;
        
        if (resubmissionContext) {
          const context = JSON.parse(resubmissionContext);
          if (context.mode === 'resubmit') {
            // Use resubmit endpoint to update existing submission
            response = await csrfFetch(`${SCRUBBER_API_BASE}/api/resubmit/${context.originalId}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                universe: [],
                timeliness: timeliness || null,
                universeRowCount: totalRows || (universe ? universe.length : 0),
                errorCountTotal: totalErrorCount || 0,
                errors: errors || [],
                selectedTable: selectedTable || {},
                filename: selectedTable?.universeType || 'universe_export',
                userEmail: userEmail
              })
            });
          } else {
            // Regular new submission
            response = await csrfFetch(`${SCRUBBER_API_BASE}/api/submit-final`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                universe: [],
                timeliness: timeliness || null,
                universeRowCount: totalRows || (universe ? universe.length : 0),
                errorCountTotal: totalErrorCount || 0,
                errors: errors || [],
                selectedTable: selectedTable || {},
                filename: selectedTable?.universeType || 'universe_export',
                userEmail: userEmail
              })
            });
          }
        } else {
          // Regular new submission
          response = await csrfFetch(`${SCRUBBER_API_BASE}/api/submit-final`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              universe: [],
                timeliness: timeliness || null,
                universeRowCount: totalRows || (universe ? universe.length : 0),
                errorCountTotal: totalErrorCount || 0,
              errors: errors || [],
              selectedTable: selectedTable || {},
              filename: selectedTable?.universeType || 'universe_export',
              userEmail: userEmail
            })
          });
        }
        
        if (!response.ok) {
          throw new Error(`Submission failed: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('✅ Submission successful:', result);
        
        // Clear resubmission context after successful submission
        if (resubmissionContext) {
          sessionStorage.removeItem('resubmissionContext');
          alert(`✅ Universe resubmitted successfully!\n\nSubmission ID: ${result.submissionId}\nStatus: ${result.status}\n\nThe existing submission has been updated with your corrections.`);
        } else {
          alert(`✅ Universe submitted successfully!\n\nSubmission ID: ${result.submissionId}\nStatus: Submitted for compliance review\n\nYou can no longer edit this universe.`);
        }
        
        // TODO: Disable editing functionality after submission
        
      }
    } catch (error) {
      console.error('❌ Error submitting universe:', error);
      alert(`❌ Failed to submit universe: ${error.message}`);
    }
  };

  const confirmSubmitWithErrors = () => {
    if (window.confirm(`You have ${totalIssues} validation errors. Are you sure you want to submit as final?`)) {
      console.log('🛠 DEBUG — Submitting universe as final (with errors)');
      // TODO: Phase 2 - implement actual submission logic
    }
    setShowSubmitDialog(false);
  };

  return (
    <div className="
      bg-gradient-to-br from-white/95 via-slate-50/80 to-orange-50/30 
      
      rounded-3xl shadow-2xl shadow-slate-200/50 
      border border-slate-200/60 backdrop-blur-xl
      mx-auto p-8 space-y-8 max-w-full
      relative overflow-hidden
      before:absolute before:inset-0 before:bg-gradient-to-br before:from-rose-500/5 before:via-orange-500/5 before:to-amber-500/5 before:opacity-0 before:hover:opacity-100 before:transition-opacity before:duration-500 before:rounded-3xl
    ">

      {/* Decorative background elements */}
      <div className="absolute top-8 right-8 w-4 h-4 bg-gradient-to-r from-rose-400 to-orange-500 rounded-full opacity-40"></div>
      <div className="absolute bottom-8 left-8 w-3 h-3 bg-gradient-to-r from-orange-400 to-amber-500 rounded-full opacity-30"></div>

      {/* Scroll Container */}
      <div className="overflow-x-auto overflow-y-auto max-h-[75vh] relative px-4 scroll-smooth before:absolute before:top-0 before:left-0 before:w-6 before:h-full before:bg-gradient-to-r before:from-slate-100/80 before:to-transparent before:pointer-events-none after:absolute after:top-0 after:right-0 after:w-6 after:h-full after:bg-gradient-to-l after:from-slate-100/80 after:to-transparent after:pointer-events-none">

        {/* Agent activity timeline (calm grey card; hides itself when no events) */}
        {submissionId && <ScrubberAgentActivity submissionId={submissionId} />}

        {/* Enhanced Header + Export Buttons */}
        <div className="flex justify-between items-center mb-6 p-6 bg-gradient-to-r from-slate-50/80 via-white to-rose-50/60 rounded-2xl border border-slate-200/60">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-rose-500/20 to-orange-500/20 border border-rose-300/50 rounded-xl">
              <div className="w-2 h-2 bg-gradient-to-r from-rose-500 to-orange-500 rounded-full"></div>
              <span className="text-sm font-bold text-rose-700">Results</span>
            </div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-900 via-rose-800 to-orange-800 bg-clip-text text-transparent">
              Annotated Universe Viewer
            </h2>
          </div>

          {/* Actions are in the main top bar - no duplicates here */}
        </div>

        {/* Enhanced Filters */}
        <div className="mb-8 p-6 bg-gradient-to-r from-slate-50/60 via-white to-rose-50/40 rounded-2xl border border-slate-200/60">
          <div className="flex flex-wrap justify-between items-center gap-6">
            <div className="flex flex-wrap gap-3">
              {categoryFilters.map((cat) => {
                const isSelected = selectedCategories.includes(cat);
                const issueCount = cat !== 'All' ? (issueCounts[cat] || 0) : 0;

                return (
                  <button
                    key={cat}
                    onClick={() => onCategoryToggle(cat)}
                    className={`
                      inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
                      text-sm font-semibold shadow-sm transition-all duration-300
                      hover:-translate-y-1 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2
                      ${isSelected
                        ? 'bg-gradient-to-r from-slate-800 via-rose-800 to-orange-800 text-white shadow-lg shadow-slate-500/25 border border-slate-700/50'
                        : 'bg-gradient-to-r from-slate-100 via-rose-50 to-orange-50 text-slate-700 hover:from-slate-200 hover:via-rose-100 hover:to-orange-100 border border-slate-300/50 hover:border-slate-400/60'
                      }
                    `}
                  >
                    <span>{cat}</span>
                    {cat !== 'All' && (
                      <span className={`
                        inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold
                        ${isSelected 
                          ? 'bg-white/20 text-white'
                          : 'bg-gradient-to-r from-rose-500 to-orange-600 text-white'
                        }
                      `}>
                        {issueCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <label className="
              inline-flex items-center gap-3 px-4 py-2.5
              bg-gradient-to-r from-slate-100/80 via-white to-rose-50/60
              rounded-xl border border-slate-300/50
              text-sm text-slate-700 font-medium
              hover:shadow-md hover:scale-105 transition-all duration-300
              cursor-pointer
            ">
              <input
                type="checkbox"
                checked={onlyShowErroredRows}
                onChange={(e) => onToggleErrors(e.target.checked)}
                className="w-4 h-4 text-rose-600 rounded border-slate-300 focus:ring-rose-500 focus:ring-2"
              />
              <span>Only show rows with issues</span>
            </label>
          </div>
        </div>

        {/* Inject children */}
        <div className="relative z-10">
          {children}
        </div>

        {/* Enhanced Footer (Issue Count) */}
        <div className="
          mt-8 p-4 bg-gradient-to-r from-slate-50/80 via-white to-rose-50/60
          rounded-2xl border border-slate-200/60
          text-right
        ">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-red-100 to-pink-100 rounded-xl border border-red-200/50">
            <div className="w-2 h-2 bg-gradient-to-r from-red-500 to-pink-600 rounded-full"></div>
            <span className="text-sm text-red-700 font-medium">
              Total Issues: <span className="font-bold text-red-800">{totalIssues}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
